import { useState } from 'react';
import { useJobProgress } from '@/context/job-progress-context';
import { JobStatus } from '@/@types/job-progress';
import { X, Loader2, CheckCircle2, AlertCircle, RefreshCw, ChevronUp, Calendar, ShieldAlert } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { FailedStoresSection } from '@/components/failed-stores-section';
import { useForceResetJobProgressMutation } from '@/services/store/store';
import { RoleEnum } from '@/enum/role';
import { getUserFromLocalStorage } from '@/utils/getUserFromLocalStorage';
import { isUserAdminInGroup } from '@/utils/user';
import { cn } from '@/lib/utils';
import { formatDateToDMY } from '@/utils/format-date-ddmmyyyy';

const ACCENT_COLOR = '#422534';

export const JobProgressInline = () => {
  const { progress, isVisible, hideProgress, refetch, isLoading } = useJobProgress();
  const [showErrors, setShowErrors] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [forceResetJobProgress, { isLoading: isClearing }] = useForceResetJobProgressMutation();

  const user = getUserFromLocalStorage();
  const moduleAccess = localStorage.getItem('@verzel:module') || '';
  const isAdmin = isUserAdminInGroup(user, moduleAccess, RoleEnum.ADMINISTRADOR);

  const handleClearForAll = async () => {
    try {
      const result = await forceResetJobProgress().unwrap();
      hideProgress();
      refetch();
      toast({
        title: 'Job limpo para todos',
        description: result.queueCleared
          ? 'O job não estava concluído: status e fila de processamento foram limpos, nada ficará processando sem acompanhamento.'
          : 'O job já estava concluído (100%). O status foi resetado e não aparecerá mais para nenhum usuário.',
      });
    } catch {
      toast({
        title: 'Erro',
        description: 'Falha ao limpar o job para todos. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  if (!isVisible) return null;
  if (!isLoading && progress?.status === JobStatus.NO_ACTIVE_JOB) return null;

  const isRunning = progress?.status === JobStatus.RUNNING;
  const isCompleted = progress?.status === JobStatus.COMPLETED;
  const isFailed = progress?.status === JobStatus.FAILED;
  const hasErrors = (progress?.failedCount || 0) > 0;

  // Limita a porcentagem a 100% (proteção contra bug do backend)
  const percentComplete = Math.min(progress?.percentComplete || 0, 100);

  const statusConfig = {
    running: {
      icon: Loader2,
      text: 'Reprocessamento em andamento',
      borderColor: 'border-[#422534]',
      bgColor: 'bg-[#422534]/5',
      iconClass: 'animate-spin',
      iconStyle: { color: ACCENT_COLOR } as React.CSSProperties,
    },
    completed: {
      icon: CheckCircle2,
      text: 'Reprocessamento concluído',
      borderColor: 'border-green-600',
      bgColor: 'bg-green-50',
      iconClass: 'text-green-600',
      iconStyle: undefined as React.CSSProperties | undefined,
    },
    failed: {
      icon: AlertCircle,
      text: 'Reprocessamento falhou',
      borderColor: 'border-red-500',
      bgColor: 'bg-red-50',
      iconClass: 'text-red-500',
      iconStyle: undefined as React.CSSProperties | undefined,
    },
    loading: {
      icon: Loader2,
      text: 'Verificando status do reprocessamento...',
      borderColor: 'border-gray-400',
      bgColor: 'bg-gray-50',
      iconClass: 'animate-spin text-gray-500',
      iconStyle: undefined as React.CSSProperties | undefined,
    },
  };

  const getStatus = () => {
    if (isLoading && !progress) return statusConfig.loading;
    if (isRunning) return statusConfig.running;
    if (isCompleted) return statusConfig.completed;
    if (isFailed) return statusConfig.failed;
    return statusConfig.loading;
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  return (
    <div
      className={cn(
        'w-full rounded-lg border-l-4 border bg-white mb-4 overflow-hidden',
        'transition-all duration-300 ease-in-out',
        status.borderColor
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 cursor-pointer',
          isExpanded && 'border-b',
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <StatusIcon className={cn('h-5 w-5', status.iconClass)} style={status.iconStyle} />
          <span className="font-semibold text-sm text-gray-800">{status.text}</span>
          {progress?.conciliationDate && (
            <span className="flex items-center gap-1 text-xs text-gray-600 bg-white/60 px-2 py-1 rounded font-medium">
              <Calendar className="h-3.5 w-3.5" />
              Lojas do dia: {formatDateToDMY(progress.conciliationDate)}
            </span>
          )}
          {!isExpanded && progress && (
            <span className="text-sm text-gray-600">
              - {percentComplete}% ({progress.successCount || 0}/{progress.totalStores || 0} lojas)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                handleClearForAll();
              }}
              disabled={isClearing}
              title="Limpar o job para todos os usuários"
            >
              <ShieldAlert className={cn('h-3.5 w-3.5 mr-1', isClearing && 'animate-pulse')} />
              Limpar para todos
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            <div className={cn('transition-transform duration-300', isExpanded ? 'rotate-0' : 'rotate-180')}>
              <ChevronUp className="h-4 w-4" />
            </div>
          </Button>
          {(isCompleted || isFailed) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                hideProgress();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div
        className={cn(
          'transition-all duration-300 ease-in-out',
          isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="px-4 py-3">
          {isLoading && !progress && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Carregando progresso...</span>
            </div>
          )}

          {progress && (
            <div className="space-y-3">
              {/* Progress info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    {progress.totalStores || 0} lojas
                  </span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {progress.successCount || 0} sucesso
                    </span>
                    {hasErrors && (
                      <span className="flex items-center gap-1 text-red-600">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {progress.failedCount} falhas
                      </span>
                    )}
                  </div>
                </div>
                <span className="font-bold text-gray-800 text-lg">{percentComplete}%</span>
              </div>

              {/* Progress bar */}
              <Progress
                value={percentComplete}
                className={cn(
                  'h-2.5',
                  isCompleted && '[&>div]:bg-green-600',
                  isFailed && '[&>div]:bg-red-500',
                  isRunning && '[&>div]:bg-[#422534]'
                )}
              />

              {/* Additional info row */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  {progress.initiatedBy && (
                    <span>
                      Iniciado por: <strong className="text-gray-700">{progress.initiatedBy.userName}</strong>
                    </span>
                  )}
                  {progress.startTime && (
                    <span>às {new Date(progress.startTime).toLocaleTimeString('pt-BR')}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {progress.estimatedTimeRemaining && isRunning && (
                    <span>Tempo restante: {progress.estimatedTimeRemaining}</span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      refetch();
                    }}
                    disabled={isLoading}
                  >
                    <RefreshCw className={cn('h-3 w-3 mr-1', isLoading && 'animate-spin')} />
                    Atualizar
                  </Button>
                </div>
              </div>

              {/* Failed stores section */}
              {hasErrors && progress.failedStores && (
                <FailedStoresSection
                  failedStores={progress.failedStores}
                  showErrors={showErrors}
                  onToggle={() => setShowErrors(!showErrors)}
                  canRetry={!isRunning}
                  conciliationDate={progress.conciliationDate}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
